import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, Phone, MapPin, RefreshCw, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cresta_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [pwd, setPwd] = useState("");
  const [authing, setAuthing] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.title = "Cresta · Admin";
  }, []);

  const fetchLeads = useCallback(
    async (authToken) => {
      const t = authToken || token;
      if (!t) return;
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/leads`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setLeads(data);
      } catch (err) {
        if (err?.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
          toast.error("Session expired. Please sign in again.");
        } else {
          toast.error("Failed to load leads.");
        }
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setAuthing(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pwd });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPwd("");
      toast.success("Signed in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password.");
    } finally {
      setAuthing(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
    setSelected(null);
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6">
        <form
          onSubmit={login}
          className="w-full max-w-md border border-[#262626] bg-[#0C0C0C] p-8 md:p-10 rounded-sm"
        >
          <div className="font-serif-display text-3xl md:text-4xl text-[#F5F5F0]">
            Cresta <span className="italic text-champagne">Admin</span>
          </div>
          <p className="mt-3 text-sm text-[#A3A3A3] font-sans-body">
            Enter the admin password to view incoming estimate requests.
          </p>
          <label className="block mt-8">
            <span className="block text-[11px] tracking-[0.22em] uppercase text-[#A3A3A3] font-sans-body mb-2">
              Password
            </span>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
              <input
                data-testid="admin-password-input"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                autoFocus
                placeholder="••••••••"
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-champagne focus:ring-1 focus:ring-[#C5A059] text-[#F5F5F0] placeholder:text-[#525252] rounded-sm pl-10 pr-4 py-3.5 outline-none transition-all font-sans-body"
              />
            </div>
          </label>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={authing}
            className="mt-6 w-full bg-champagne text-[#0A0A0A] text-[12px] tracking-[0.22em] uppercase font-semibold px-7 py-4 rounded-sm hover:bg-[#D4AF37] disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {authing ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0]">
      <header className="border-b border-white/5 bg-[#0A0A0A]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="font-serif-display text-2xl">
            Cresta <span className="italic text-champagne">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchLeads()}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#A3A3A3] hover:text-champagne px-3 py-2"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#A3A3A3] hover:text-champagne px-3 py-2"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="overline">Inbox</span>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">
              Estimate <span className="italic text-champagne">requests</span>
            </h1>
          </div>
          <div className="text-sm text-[#A3A3A3] font-sans-body" data-testid="admin-lead-count">
            {leads.length} total
          </div>
        </div>

        {loading && leads.length === 0 ? (
          <div className="border border-[#262626] bg-[#0C0C0C] rounded-sm p-16 text-center text-[#A3A3A3]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : leads.length === 0 ? (
          <div className="border border-[#262626] bg-[#0C0C0C] rounded-sm p-16 text-center">
            <p className="text-[#A3A3A3] font-sans-body">
              No leads yet. Submissions from the Free Estimate form will appear here.
            </p>
          </div>
        ) : (
          <div className="border border-[#262626] bg-[#0C0C0C] rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-leads-table">
                <thead>
                  <tr className="text-[10px] tracking-[0.22em] uppercase text-[#A3A3A3] font-sans-body">
                    <th className="text-left p-4 font-medium">Date</th>
                    <th className="text-left p-4 font-medium">Name</th>
                    <th className="text-left p-4 font-medium">Project</th>
                    <th className="text-left p-4 font-medium hidden md:table-cell">Budget</th>
                    <th className="text-left p-4 font-medium hidden lg:table-cell">Timeline</th>
                    <th className="text-left p-4 font-medium hidden md:table-cell">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr
                      key={l.id}
                      onClick={() => setSelected(l)}
                      data-testid={`admin-lead-row-${l.id}`}
                      className="border-t border-[#1A1A1A] hover:bg-[#111] cursor-pointer transition-colors"
                    >
                      <td className="p-4 text-[#A3A3A3] text-sm whitespace-nowrap">
                        {new Date(l.created_at).toLocaleDateString("en-CA", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </td>
                      <td className="p-4">
                        <div className="text-[#F5F5F0] font-medium">{l.name}</div>
                        <div className="text-[#525252] text-xs mt-0.5">{l.neighborhood || "—"}</div>
                      </td>
                      <td className="p-4 text-[#F5F5F0] text-sm">{l.project_type}</td>
                      <td className="p-4 text-[#A3A3A3] text-sm hidden md:table-cell">{l.budget_range || "—"}</td>
                      <td className="p-4 text-[#A3A3A3] text-sm hidden lg:table-cell">{l.timeline || "—"}</td>
                      <td className="p-4 text-sm hidden md:table-cell">
                        <a href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()} className="text-champagne hover:underline block">{l.email}</a>
                        <a href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()} className="text-[#A3A3A3] hover:text-champagne text-xs">{l.phone}</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="admin-lead-drawer"
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-lg bg-[#0C0C0C] border-l border-[#262626] h-full overflow-y-auto p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <span className="overline">Lead</span>
                <h2 className="font-serif-display text-3xl mt-2">{selected.name}</h2>
                <div className="text-sm text-[#A3A3A3] mt-1">{selected.project_type}</div>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="admin-drawer-close"
                className="text-[#A3A3A3] hover:text-champagne text-xl px-3"
              >
                ✕
              </button>
            </div>

            <div className="mt-8 space-y-4 text-sm">
              <Row icon={Mail} label="Email" value={selected.email} href={`mailto:${selected.email}`} />
              <Row icon={Phone} label="Phone" value={selected.phone} href={`tel:${selected.phone}`} />
              <Row icon={MapPin} label="Neighborhood" value={selected.neighborhood || "—"} />
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
              <Cell label="Budget" value={selected.budget_range || "—"} />
              <Cell label="Timeline" value={selected.timeline || "—"} />
              <Cell label="Submitted" value={new Date(selected.created_at).toLocaleString("en-CA")} />
              <Cell label="Status" value={selected.status} />
            </div>

            {selected.message && (
              <div className="mt-8">
                <div className="overline mb-3">Message</div>
                <p className="text-[#F5F5F0] font-sans-body leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
            )}

            <a
              href={`mailto:${selected.email}?subject=Cresta Bath %26 Tile — Your estimate request`}
              data-testid="admin-reply-cta"
              className="mt-10 block text-center bg-champagne text-[#0A0A0A] text-[12px] tracking-[0.22em] uppercase font-semibold px-7 py-4 rounded-sm hover:bg-[#D4AF37]"
            >
              Reply to {selected.name.split(" ")[0]}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-center gap-3 group">
      <span className="w-9 h-9 border border-[#262626] flex items-center justify-center rounded-sm bg-[#0A0A0A] group-hover:border-champagne transition-colors">
        <Icon className="w-4 h-4 text-champagne" />
      </span>
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#A3A3A3]">{label}</div>
        <div className="text-[#F5F5F0]">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}

function Cell({ label, value }) {
  return (
    <div className="border border-[#262626] bg-[#0A0A0A] p-4 rounded-sm">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#A3A3A3]">{label}</div>
      <div className="text-[#F5F5F0] mt-1.5 text-sm">{value}</div>
    </div>
  );
}
